import React from 'react';

import { Messages } from '../../../../../gen-src/Localize';
import { LensOrderField, LensesDisplayGridRowDataFragment } from '../../../../../gen-src/queries';
import {
  ColumnFactory,
  createSortableHeaderFormatter,
  ExplorerStyledDisplayGrid,
  GraphQLError,
  GridSortProps,
  reportGraphQLErrors,
  TableauUser,
  UnsortableColumnKey,
  UsualDisplayGridProps,
} from '../../../data-catalog-shared';
import { createNumberCellFormatter } from '../datagrid/cellFormatters/NumberCellFormatter';
import { createObfuscatedLinkCellFormatter } from '../datagrid/cellFormatters/ObfuscatedCellFormatter';
import { createUserNameLinkCellFormatter } from '../datagrid/cellFormatters/UserNameCell';
import { LensesValidSortFields } from '../sorting/sortProviders/LensesGridSortProvider';

const shouldBeObfuscated: (rowData: LensesDisplayGridRowData) => boolean = ({ name }) => name === null;

export interface LensesDisplayGridRowData {
  readonly id: string;
  readonly name: string | null;
  readonly description: string | null;
  readonly vizportalUrlId: string;
  readonly projectVizportalUrlId: string | null;
  readonly updatedAt: string;
  readonly workbookCount: number | null;
  readonly owner: TableauUser;
}

export function getRowDataForLens(lens: LensesDisplayGridRowDataFragment): LensesDisplayGridRowData {
  return {
    id: lens.id,
    name: lens.name,
    description: lens.description,
    vizportalUrlId: lens.vizportalUrlId,
    projectVizportalUrlId: lens.projectVizportalUrlId,
    owner: lens.owner,
    workbookCount: lens.downstreamWorkbooksConnection?.totalCount || 0,
    updatedAt: lens.updatedAt as string,
  };
}

type LensesColumnFactory = ColumnFactory<
  GridSortProps<LensesValidSortFields>,
  LensesDisplayGridRowData,
  LensesValidSortFields
>;

function columns(): ReadonlyArray<LensesColumnFactory> {
  const cols: ReadonlyArray<LensesColumnFactory> = [
    (sortParams) => ({
      name: 'name',
      dataKey: LensOrderField.NAME,
      headerText: Messages.GridHeaderName(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createObfuscatedLinkCellFormatter(
        shouldBeObfuscated,
        ({ name }) => name,
        (urls, { vizportalUrlId }) => urls.lensLineagePath(vizportalUrlId)
      ),
      resizable: true,
      sortable: true,
    }),
    (sortParams) => ({
      name: 'downstreamWorkbooks',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderConnectionCountWorkbooks(),
      headerFormatter: createSortableHeaderFormatter(sortParams, 'right'),
      cellFormatter: createNumberCellFormatter<LensesDisplayGridRowData>(({ workbookCount }) => workbookCount),
      resizable: true,
      sortable: false,
    }),
    (sortParams) => ({
      name: 'owner',
      dataKey: UnsortableColumnKey,
      headerText: Messages.GridHeaderOwner(),
      headerFormatter: createSortableHeaderFormatter(sortParams),
      cellFormatter: createUserNameLinkCellFormatter(({ owner }) => owner),
      resizable: true,
      sortable: false,
    }),
  ];

  return cols.filter(Boolean) as ReadonlyArray<LensesColumnFactory>;
}

export type LensesDisplayGridProps = UsualDisplayGridProps<LensesDisplayGridRowData, GraphQLError> &
  GridSortProps<LensesValidSortFields>;

export function LensesDisplayGrid(props: LensesDisplayGridProps): JSX.Element {
  const cols = columns().map((columnFactory) => columnFactory(props));

  return (
    <ExplorerStyledDisplayGrid
      columns={cols}
      rowStore={props.rowStore}
      emptyGridMessage={props.emptyGridMessage}
      rowCount={props.rowCount}
      renderErrors={reportGraphQLErrors}
      orderBy={props.orderBy}
      updateSortOrder={props.updateSortOrder}
    />
  );
}
